// Settings › Campaigns (BL-008, AdTrack's attribution): the company's marketing campaigns, and the
// tracking numbers whose calls are credited to each. The main line is a tracking number too, under
// its own campaign. A call keeps the campaign it rang under, so a number moved or removed changes
// only calls from then on. Where a number rings through to (BL-001) is kept here too, with what happens
// when nobody answers and the blocked numbers (Forwarding.tsx), but it applies only once the number's
// Voice URL at Twilio points at Flowology.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  CHANNEL_WORDS,
  CHANNELS,
  campaignMeans,
  campaignOrder,
  forwardMeans,
  numberMeans,
  type Campaign,
  type Channel,
  type TrackingNumber,
} from './campaigns-model.ts';
import { AdSpendCards } from './AdSpend.tsx';
import { BlockedNumbers, UnansweredCalls } from './Forwarding.tsx';
import { SiteCapture } from './SiteCapture.tsx';
import './campaigns.css';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');

type CampaignDraft = { id?: string; name: string; channel: Channel; active: boolean; rowVersion?: number };
type NumberDraft = { phone: string; label: string; campaignId: string; forwardTo: string; isNew: boolean };

/** Riverstone's page; Blueprint's is AdsPage.tsx. */
export function CampaignsTab({ canEdit }: { canEdit: boolean }) {
  // the campaigns are ServiceTitan's while the company mirrors it; tracking numbers stay ours (st-mirror §2)
  const mirroring = useMirror();
  const editCampaigns = canEdit && !mirroring;
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [numbers, setNumbers] = useState<TrackingNumber[] | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<CampaignDraft | null>(null);
  const [numberDraft, setNumberDraft] = useState<NumberDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    void Promise.all([
      client.call<{ campaigns: Campaign[] }>('campaigns.list', { includeRetired: true }),
      client.call<{ numbers: TrackingNumber[] }>('trackingNumbers.list', {}),
    ])
      .then(([c, n]) => {
        setCampaigns(campaignOrder(c.campaigns));
        setNumbers(n.numbers);
      })
      .catch((e: unknown) => setError(say(e)));
  }, [again]);

  const write = async (run: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      setSaid(await run());
      setDraft(null);
      setNumberDraft(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const saveCampaign = (d: CampaignDraft) =>
    write(async () => {
      const r = d.id
        ? await client.call<Campaign>('campaigns.update', {
            id: d.id,
            rowVersion: d.rowVersion,
            name: d.name.trim(),
            channel: d.channel,
            active: d.active,
          })
        : await client.call<Campaign>('campaigns.create', { name: d.name.trim(), channel: d.channel });
      return `${r.name} saved.`;
    });
  const saveNumber = (d: NumberDraft) =>
    write(async () => {
      const r = await client.call<TrackingNumber>('trackingNumbers.assign', {
        phone: d.phone.trim(),
        campaignId: d.campaignId || null,
        label: d.label.trim() || null,
        forwardTo: d.forwardTo.trim() || null,
      });
      return `${r.text} saved. ${numberMeans(r)}.`;
    });
  const removeNumber = (n: TrackingNumber) =>
    write(async () => {
      await client.call('trackingNumbers.remove', { phone: n.phone });
      return `${n.text} removed. Its past calls keep their campaign.`;
    });
  const live = (campaigns ?? []).filter((c) => c.active);

  // the campaigns and their numbers
  const cards = (
    <>
      <section className="stone card" aria-label="Campaigns">
        <div className="row between wrap">
          <h2 className="h3">Campaigns</h2>
          {editCampaigns && !draft && (
            <button
              type="button"
              className="btn"
              onClick={() => setDraft({ name: '', channel: 'google_ads', active: true })}
            >
              Add a campaign
            </button>
          )}
        </div>
        <p className="muted">
          Where calls and work come from. A call is credited to the campaign of the number it dialled; the
          office can book it under another when the caller says how they found you.
        </p>
        {mirroring && canEdit && <p className="muted small">{MIRROR_LINE}</p>}
        {draft && editCampaigns && (
          <CampaignForm
            draft={draft}
            busy={busy}
            onChange={setDraft}
            onSave={() => void saveCampaign(draft)}
            onCancel={() => setDraft(null)}
          />
        )}
        {!campaigns && !error && <p className="muted">Getting the campaigns…</p>}
        {campaigns && !campaigns.length && <p className="muted">No campaigns yet.</p>}
        {campaigns && campaigns.length > 0 && (
          <ul className="campaign-list">
            {campaigns.map((c) => (
              <li key={c.id} className={c.active ? '' : 'retired'}>
                <div>
                  <b>{c.name}</b>
                  <span className="muted small block">{campaignMeans(c)}</span>
                </div>
                {editCampaigns && (
                  <button
                    type="button"
                    className="btn soft"
                    aria-label={`Change ${c.name}`}
                    onClick={() => setDraft({ ...c })}
                  >
                    Change
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="stone card" aria-label="Tracking numbers">
        <div className="row between wrap">
          <h2 className="h3">Tracking numbers</h2>
          {canEdit && !numberDraft && (
            <button
              type="button"
              className="btn"
              onClick={() =>
                setNumberDraft({
                  phone: '',
                  label: '',
                  campaignId: live[0]?.id ?? '',
                  forwardTo: '',
                  isNew: true,
                })
              }
            >
              Add a number
            </button>
          )}
        </div>
        <p className="muted">
          Each number’s calls are credited to its campaign from now on; calls already taken keep theirs. Add
          your main line too, under its own campaign, or its calls show as “No campaign”.
        </p>
        {numberDraft && (
          <NumberForm
            draft={numberDraft}
            campaigns={campaigns ?? []}
            busy={busy}
            onChange={setNumberDraft}
            onSave={() => void saveNumber(numberDraft)}
            onCancel={() => setNumberDraft(null)}
          />
        )}
        {!numbers && !error && <p className="muted">Getting the numbers…</p>}
        {numbers && !numbers.length && <p className="muted">No tracking numbers yet.</p>}
        {numbers && numbers.length > 0 && (
          <ul className="campaign-list">
            {numbers.map((n) => {
              const change = () =>
                setNumberDraft({
                  phone: n.text,
                  label: n.label ?? '',
                  campaignId: n.campaign?.id ?? '',
                  forwardTo: n.forwardText ?? '',
                  isNew: false,
                });
              const forward = forwardMeans(n);
              const words = (
                <>
                  <b>{n.text}</b>
                  {n.label && <span className="muted"> · {n.label}</span>}
                  <span className="muted small block">{numberMeans(n)}</span>
                  {forward && <span className="muted small block">{forward}</span>}
                </>
              );
              return (
                <li key={n.phone}>
                  <div>{words}</div>
                  {canEdit && (
                    <div className="row">
                      <button
                        type="button"
                        className="btn soft"
                        aria-label={`Change ${n.text}`}
                        onClick={change}
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        className="btn quiet"
                        aria-label={`Remove ${n.text}`}
                        disabled={busy}
                        onClick={() => void removeNumber(n)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <UnansweredCalls canEdit={canEdit} />
      <BlockedNumbers canEdit={canEdit} />
      {/* the website tag: its clicks, and each campaign's number on the site (BL-092) */}
      <SiteCapture canEdit={canEdit} numbers={numbers ?? []} />
    </>
  );
  const notes = (
    <>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </>
  );
  return (
    <div className="campaigns">
      {notes}
      {cards}
      <AdSpendCards canEdit={canEdit} campaigns={campaigns ?? []} />
    </div>
  );
}

function CampaignForm({
  draft,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: CampaignDraft;
  busy: boolean;
  onChange: (d: CampaignDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <form
      className="fgrid campaign-form"
      aria-label={draft.id ? `Change ${draft.name}` : 'A new campaign'}
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.name.trim() && !busy) onSave();
      }}
    >
      <label>
        <span className="flabel">Name</span>
        <input
          className="field"
          value={draft.name}
          maxLength={100}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Channel</span>
        <select
          className="field"
          value={draft.channel}
          onChange={(e) => onChange({ ...draft, channel: e.target.value as Channel })}
        >
          {CHANNELS.map((ch) => (
            <option key={ch} value={ch}>
              {CHANNEL_WORDS[ch]}
            </option>
          ))}
        </select>
      </label>
      {draft.id && (
        <label className="check wide">
          <input
            type="checkbox"
            checked={!draft.active}
            onChange={(e) => onChange({ ...draft, active: !e.target.checked })}
          />
          Retired: calls and jobs keep it, but nobody books under it again
        </label>
      )}
      <div className="row wide">
        <SaveButton
          className="btn primary"
          busy={busy}
          busyLabel="Saving…"
          disabled={!draft.name.trim()}
          onClick={onSave}
        >
          Save
        </SaveButton>
        <button type="button" className="btn quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function NumberForm({
  draft,
  campaigns,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: NumberDraft;
  campaigns: Campaign[];
  busy: boolean;
  onChange: (d: NumberDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <form
      className="fgrid campaign-form"
      aria-label={draft.isNew ? 'A new tracking number' : `Change ${draft.phone}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.phone.trim() && !busy) onSave();
      }}
    >
      <label>
        <span className="flabel">Number</span>
        <input
          className="field"
          type="tel"
          value={draft.phone}
          readOnly={!draft.isNew}
          maxLength={40}
          onChange={(e) => onChange({ ...draft, phone: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Label</span>
        <input
          className="field"
          value={draft.label}
          maxLength={100}
          placeholder="Google Ads – drains"
          onChange={(e) => onChange({ ...draft, label: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Rings through to (optional)</span>
        <input
          className="field"
          type="tel"
          value={draft.forwardTo}
          maxLength={40}
          placeholder="The main line when empty"
          onChange={(e) => onChange({ ...draft, forwardTo: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Calls to it are credited to</span>
        <select
          className="field"
          value={draft.campaignId}
          onChange={(e) => onChange({ ...draft, campaignId: e.target.value })}
        >
          <option value="">No campaign</option>
          {campaigns
            // a retired campaign only when the number is under it still
            .filter((c) => c.active || c.id === draft.campaignId)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.active ? c.name : `${c.name} (retired)`}
              </option>
            ))}
        </select>
      </label>
      <div className="row wide">
        <SaveButton
          className="btn primary"
          busy={busy}
          busyLabel="Saving…"
          disabled={!draft.phone.trim()}
          onClick={onSave}
        >
          Save
        </SaveButton>
        <button type="button" className="btn quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
